import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * @test-file: Form.test.tsx
 * @test-coverage: 17 tests (Group, Label, Control, Select, Check, Text sub-components)
 * @last-updated: 2025-12-28
 */
import React from 'react';
// ===== FORM.GROUP =====
const FormGroup = ({ controlId, children, className = '', style }) => {
    return (_jsx("div", { className: `ui-form-group ${className}`, id: controlId ? `${controlId}-group` : undefined, style: style, children: controlId
            ? React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                    // Pass controlId to Label (as htmlFor) and Control (as id)
                    if (child.type === FormLabel) {
                        return React.cloneElement(child, {
                            htmlFor: controlId,
                        });
                    }
                    if (child.type === FormControl || child.type === FormSelect || child.type === FormCheck) {
                        return React.cloneElement(child, {
                            id: controlId,
                        });
                    }
                }
                return child;
            })
            : children }));
};
// ===== FORM.LABEL =====
const FormLabel = ({ children, className = '', ...props }) => {
    return (_jsx("label", { className: `ui-form-label ${className}`, ...props, children: children }));
};
// ===== FORM.CONTROL =====
const FormControl = React.forwardRef(({ as = 'input', isInvalid, isValid, className = '', ...props }, ref) => {
    const validationClass = isInvalid ? 'is-invalid' : isValid ? 'is-valid' : '';
    if (as === 'textarea') {
        return (_jsx("textarea", { ref: ref, className: `ui-form-control ${validationClass} ${className}`, ...props }));
    }
    return (_jsx("input", { ref: ref, className: `ui-form-control ${validationClass} ${className}`, ...props }));
});
FormControl.displayName = 'FormControl';
// ===== FORM.SELECT =====
const FormSelect = React.forwardRef(({ isInvalid, isValid, children, className = '', ...props }, ref) => {
    const validationClass = isInvalid ? 'is-invalid' : isValid ? 'is-valid' : '';
    return (_jsx("select", { ref: ref, className: `ui-form-select ${validationClass} ${className}`, ...props, children: children }));
});
FormSelect.displayName = 'FormSelect';
// ===== FORM.CHECK =====
const FormCheck = React.forwardRef(({ type = 'checkbox', label, isInvalid, isValid, className = '', ...props }, ref) => {
    const validationClass = isInvalid ? 'is-invalid' : isValid ? 'is-valid' : '';
    return (_jsxs("div", { className: `ui-form-check ${className}`, children: [_jsx("input", { ref: ref, type: type, className: `ui-form-check-input ${validationClass}`, ...props }), label && (_jsx("label", { className: "ui-form-check-label", htmlFor: props.id, children: label }))] }));
});
FormCheck.displayName = 'FormCheck';
// ===== FORM.TEXT =====
const FormText = ({ muted = true, children, className = '', style }) => {
    const mutedClass = muted ? 'text-muted' : '';
    return _jsx("small", { className: `ui-form-text ${mutedClass} ${className}`, style: style, children: children });
};
// ===== NAMESPACE EXPORT =====
export const Form = Object.assign(({ children, ...props }) => (_jsxs(_Fragment, { children: [_jsx("form", { ...props, children: children }), _jsx("style", { children: `
        /* Form Group */
        .ui-form-group {
          margin-bottom: 16px;
        }

        /* Form Label */
        .ui-form-label {
          display: block;
          margin-bottom: 6px;
          font-weight: 600;
          font-size: 14px;
          color: var(--text);
        }

        /* Form Control (Input/Textarea) */
        .ui-form-control {
          width: 100%;
          padding: 10px 12px;
          font-size: 14px;
          line-height: 1.5;
          color: var(--text);
          background: var(--card, rgba(255, 255, 255, 0.04));
          border: 1px solid var(--border);
          border-radius: 8px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .ui-form-control:focus {
          outline: none;
          border-color: var(--accent, #3b82f6);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .ui-form-control::placeholder {
          color: var(--muted, #64748b);
          opacity: 0.6;
        }

        .ui-form-control.is-invalid {
          border-color: #ef4444;
        }

        .ui-form-control.is-invalid:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .ui-form-control.is-valid {
          border-color: #22c55e;
        }

        .ui-form-control.is-valid:focus {
          border-color: #22c55e;
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
        }

        /* Textarea */
        textarea.ui-form-control {
          resize: vertical;
          min-height: 80px;
        }

        /* Form Select */
        .ui-form-select {
          width: 100%;
          padding: 10px 12px;
          font-size: 14px;
          line-height: 1.5;
          color: var(--text);
          background: var(--card, rgba(255, 255, 255, 0.04));
          border: 1px solid var(--border);
          border-radius: 8px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          cursor: pointer;
        }

        .ui-form-select:focus {
          outline: none;
          border-color: var(--accent, #3b82f6);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .ui-form-select.is-invalid {
          border-color: #ef4444;
        }

        .ui-form-select.is-invalid:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .ui-form-select.is-valid {
          border-color: #22c55e;
        }

        .ui-form-select.is-valid:focus {
          border-color: #22c55e;
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
        }

        /* Form Check (Checkbox/Radio) */
        .ui-form-check {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }

        .ui-form-check-input {
          width: 18px;
          height: 18px;
          border: 2px solid var(--border);
          border-radius: 4px;
          cursor: pointer;
          transition: border-color 0.15s ease, background-color 0.15s ease;
        }

        .ui-form-check-input[type='radio'] {
          border-radius: 50%;
        }

        .ui-form-check-input:checked {
          background-color: var(--brand, #ffb703);
          border-color: var(--brand, #ffb703);
        }

        .ui-form-check-input:focus {
          outline: 2px solid var(--accent, #3b82f6);
          outline-offset: 2px;
        }

        .ui-form-check-input.is-invalid {
          border-color: #ef4444;
        }

        .ui-form-check-input.is-valid {
          border-color: #22c55e;
        }

        .ui-form-check-label {
          font-size: 14px;
          color: var(--text);
          cursor: pointer;
          user-select: none;
        }

        /* Form Text (Help text) */
        .ui-form-text {
          display: block;
          margin-top: 4px;
          font-size: 12px;
          line-height: 1.4;
        }

        .ui-form-text.text-muted {
          color: var(--muted, #64748b);
        }

        .text-danger {
          color: #ef4444;
        }

        .text-success {
          color: #22c55e;
        }
      ` })] })), {
    Group: FormGroup,
    Label: FormLabel,
    Control: FormControl,
    Select: FormSelect,
    Check: FormCheck,
    Text: FormText,
});
